import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useLocation } from 'react-router-dom';
import Navigation from '../components/Navigation';
import { usePageView } from '../hooks/usePageView';
import ShowModal from '../components/ShowModal';
import ShareReviewModal from '../components/ShareReviewModal';
import ShowReviewsModal from '../components/ShowReviewsModal';
import RecommendShowModal from '../components/RecommendShowModal';
import EmailRecommendModal from '../components/EmailRecommendModal';
import ShowFinderBanner from '../components/ShowFinderBanner';
import { ServiceLabel } from '../contexts/StreamingServicesContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { CheckCircle, Edit2, Share2, Trash2, Grid, Eye, Clock, Check, Bookmark, LayoutGrid, List, ChevronDown, ChevronUp, SlidersHorizontal, Send, Mail } from 'lucide-react';
import SEO from '../components/SEO';
import styles from './MyShows.module.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MyShows = ({ user, setUser }) => {
  usePageView('my-shows');
  const location = useLocation();
  const [shows, setShows] = useState([]);
  const [filteredShows, setFilteredShows] = useState([]);
  const [showStatistics, setShowStatistics] = useState({});
  const [currentFilter, setCurrentFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date-newest'); // default sort
  const [viewMode, setViewMode] = useState(() => {
    // Default to list view on mobile (768px or less), grid on desktop
    return window.innerWidth <= 768 ? 'list' : 'grid';
  });
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'
  const [editingShow, setEditingShow] = useState(null);
  const [sharingReview, setSharingReview] = useState(null);
  const [selectedShowForReviews, setSelectedShowForReviews] = useState(null);
  const [recommendingShow, setRecommendingShow] = useState(null);
  const [emailRecommendingShow, setEmailRecommendingShow] = useState(null);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [deletingShowId, setDeletingShowId] = useState(null);
  const [showToDelete, setShowToDelete] = useState(null); // For confirmation dialog

  // Helper function to get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case 'Currently Watching':
        return <Eye size={20} />;
      case 'Waiting for Next Season':
        return <Clock size={20} />;
      case 'Completed':
        return <Check size={20} />;
      case 'Want to Watch':
        return <Bookmark size={20} />;
      default:
        return <Grid size={20} />;
    }
  };

  // Handle URL parameters for status filter from Dashboard
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const status = params.get('status');
    if (status) {
      setCurrentFilter(status);
    }
  }, [location]);

  useEffect(() => {
    loadShows();
    // Refresh when shows are added/changed elsewhere (e.g. Show Finder wizard)
    const handler = () => loadShows();
    window.addEventListener('baadtv:shows-changed', handler);
    return () => window.removeEventListener('baadtv:shows-changed', handler);
  }, []);

  useEffect(() => {
    filterAndSortShows();
  }, [shows, currentFilter, sortBy, searchQuery]);

  const loadShows = async () => {
    try {
      const response = await axios.get(`${API}/shows`, { withCredentials: true });
      const showsData = response.data;
      setShows(showsData);

      // Load statistics for each show
      const statsPromises = showsData.map(show =>
        axios.get(`${API}/shows/${encodeURIComponent(show.title)}/statistics`, { withCredentials: true })
          .then(res => ({ title: show.title, stats: res.data }))
          .catch(err => {
            console.error(`Error loading stats for ${show.title}:`, err);
            return { title: show.title, stats: null };
          })
      );

      const statsResults = await Promise.all(statsPromises);
      const statsMap = {};
      statsResults.forEach(result => {
        if (result.stats) {
          statsMap[result.title] = result.stats;
        }
      });
      setShowStatistics(statsMap);
    } catch (error) {
      console.error('Error loading shows:', error);
    }
  };

  const filterAndSortShows = () => {
    // Filter shows by status
    let filtered = [];
    if (currentFilter === 'all') {
      filtered = [...shows];
    } else if (currentFilter === 'watching') {
      filtered = shows.filter(s => s.status === 'Currently Watching');
    } else if (currentFilter === 'waiting') {
      filtered = shows.filter(s => s.status === 'Waiting for Next Season');
    } else if (currentFilter === 'completed') {
      filtered = shows.filter(s => s.status === 'Completed');
    } else if (currentFilter === 'want') {
      filtered = shows.filter(s => s.status === 'Want to Watch');
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(show => 
        show.title.toLowerCase().includes(query) ||
        show.streaming_service?.toLowerCase().includes(query) ||
        show.custom_service?.toLowerCase().includes(query) ||
        show.genres?.some(genre => genre.toLowerCase().includes(query))
      );
    }

    // Sort the filtered shows
    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'date-newest':
          return new Date(b.created_at) - new Date(a.created_at);
        case 'date-oldest':
          return new Date(a.created_at) - new Date(b.created_at);
        case 'rating-highest':
          // Handle shows without ratings
          const ratingA = a.personal_rating || 0;
          const ratingB = b.personal_rating || 0;
          return ratingB - ratingA;
        case 'rating-lowest':
          const ratingA2 = a.personal_rating || 11; // Put unrated at end
          const ratingB2 = b.personal_rating || 11;
          return ratingA2 - ratingB2;
        case 'title-az':
          return a.title.localeCompare(b.title);
        case 'title-za':
          return b.title.localeCompare(a.title);
        case 'release-newest':
          // Handle shows without release dates
          const dateA = a.release_date || '1900-01-01';
          const dateB = b.release_date || '1900-01-01';
          return dateB.localeCompare(dateA);
        case 'release-oldest':
          const dateA2 = a.release_date || '9999-12-31';
          const dateB2 = b.release_date || '9999-12-31';
          return dateA2.localeCompare(dateB2);
        default:
          return 0;
      }
    });

    setFilteredShows(sorted);
  };

  // Show delete confirmation dialog
  const handleDeleteClick = (show) => {
    console.log('Delete button clicked for show:', show.title, show.id);
    setShowToDelete(show);
  };

  // Actually perform the delete
  const handleDelete = async () => {
    if (!showToDelete) return;
    
    const showId = showToDelete.id;
    console.log('Confirmed delete for show ID:', showId);
    
    // Prevent multiple delete operations
    if (deletingShowId) {
      console.log('Delete operation already in progress');
      return;
    }

    console.log('Making DELETE API call...');
    setDeletingShowId(showId);
    setShowToDelete(null); // Close confirmation dialog
    
    try {
      const response = await axios.delete(`${API}/shows/${showId}`, { withCredentials: true });
      console.log('Delete API response:', response);
      
      toast.success('Show deleted successfully');
      loadShows();
    } catch (error) {
      console.error('Error deleting show:', error);
      
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.');
      } else if (error.response?.status === 404) {
        toast.error('Show not found. It may have already been deleted.');
      } else {
        toast.error('Error deleting show. Please try again.');
      }
    } finally {
      setDeletingShowId(null); // Clear loading state
    }
  };

  const handleAddShow = () => {
    setModalMode('add');
    setEditingShow(null);
    setShowModal(true);
  };

  const handleEdit = (show) => {
    setModalMode('edit');
    setEditingShow(show);
    setShowModal(true);
  };

  const handleShare = (show) => {
    setSharingReview(show);
  };

  const handleShowReviews = (show) => {
    // Transform show data to match the format expected by ShowReviewsModal
    const showData = {
      show_title: show.title,
      show_image_url: show.image_url,
      backdrop_url: show.backdrop_url,
      streaming_service: show.streaming_service,
      custom_service: show.custom_service,
      overview: show.overview,
      // Episode tracking data
      show_status: show.show_status,
      in_production: show.in_production,
      next_episode: show.next_episode,
      last_episode: show.last_episode,
      release_date: show.release_date,
      genres: show.genres
    };
    setSelectedShowForReviews(showData);
  };

  const handleMarkCompleted = async (show) => {
    try {
      await axios.put(
        `${API}/shows/${show.id}`,
        { status: 'Completed' },
        { withCredentials: true }
      );
      loadShows();
    } catch (error) {
      console.error('Error marking show as completed:', error);
      toast.error('Error updating show. Please try again.');
    }
  };

  return (
    <>
      <SEO 
        title="My Shows - TV Collection | baad.tv"
        description={`Manage your TV show collection. ${shows.length} shows tracked across all streaming services. Rate, review, and organize your watchlist.`}
        url="/my-shows"
      />
      <div className={styles.myShowsContainer}>
        <Navigation user={user} setUser={setUser} currentPage="my-shows" />
      
      <div className={styles.myShowsContent}>
        <ShowFinderBanner />
        <div className={styles.pageHeader}>
          <div>
            <h1 data-testid="my-shows-title">My BaadTV</h1>
            <p>Manage your shows here</p>
          </div>
          <Button 
            data-testid="add-show-btn" 
            onClick={handleAddShow}
            size="lg"
            style={{
              backgroundColor: '#E62F88',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#E48BB6'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#E62F88'}
          >
            + Add Show
          </Button>
        </div>

        {/* Desktop: Show sort and filters inline */}
        <div className={styles.desktopFilters}>
          {/* Search Bar */}
          <div className={styles.searchContainer}>
            <input
              type="text"
              placeholder="Search shows by title, genre, or streaming service..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={styles.searchInput}
            />
            {searchQuery && (
              <button
                className={styles.clearSearch}
                onClick={() => setSearchQuery('')}
                title="Clear search"
              >
                ×
              </button>
            )}
          </div>

          <div className={styles.sortContainer}>
            <label htmlFor="sort-select" className={styles.sortLabel}>Sort by:</label>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger id="sort-select" className={styles.sortSelect}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="date-newest">Date Added (Newest First)</SelectItem>
                <SelectItem value="date-oldest">Date Added (Oldest First)</SelectItem>
                <SelectItem value="rating-highest">Your Rating (Highest First)</SelectItem>
                <SelectItem value="rating-lowest">Your Rating (Lowest First)</SelectItem>
                <SelectItem value="title-az">Title (A-Z)</SelectItem>
                <SelectItem value="title-za">Title (Z-A)</SelectItem>
                <SelectItem value="release-newest">Release Date (Newest First)</SelectItem>
                <SelectItem value="release-oldest">Release Date (Oldest First)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className={styles.filterRow}>
            <div className={styles.filterIcons}>
              <button 
                className={`${styles.filterIcon} ${currentFilter === 'all' ? styles.active : ''}`}
                onClick={() => setCurrentFilter('all')}
                title="All Shows"
              >
                <Grid size={20} />
              </button>
              <button 
                className={`${styles.filterIcon} ${currentFilter === 'watching' ? styles.active : ''}`}
                onClick={() => setCurrentFilter('watching')}
                title="Watching"
              >
                <Eye size={20} />
              </button>
              <button 
                className={`${styles.filterIcon} ${currentFilter === 'waiting' ? styles.active : ''}`}
                onClick={() => setCurrentFilter('waiting')}
                title="Waiting for New Season"
              >
                <Clock size={20} />
              </button>
              <button 
                className={`${styles.filterIcon} ${currentFilter === 'completed' ? styles.active : ''}`}
                onClick={() => setCurrentFilter('completed')}
                title="Completed"
              >
                <Check size={20} />
              </button>
              <button 
                className={`${styles.filterIcon} ${currentFilter === 'want' ? styles.active : ''}`}
                onClick={() => setCurrentFilter('want')}
                title="Want to Watch"
              >
                <Bookmark size={20} />
              </button>
            </div>

            <div className={styles.viewToggle}>
              <button 
                className={`${styles.viewIcon} ${viewMode === 'grid' ? styles.active : ''}`}
                onClick={() => setViewMode('grid')}
                title="Grid View"
              >
                <LayoutGrid size={20} />
              </button>
              <button 
                className={`${styles.viewIcon} ${viewMode === 'list' ? styles.active : ''}`}
                onClick={() => setViewMode('list')}
                title="List View"
              >
                <List size={20} />
              </button>
            </div>
          </div>
        </div>

        {/* Mobile: Collapsible filters */}
        <div className={styles.mobileFilters}>
          <button 
            className={styles.mobileFilterToggle}
            onClick={() => setFiltersExpanded(!filtersExpanded)}
          >
            <SlidersHorizontal size={20} />
            <span>Filters & Sort</span>
            {filtersExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </button>

          {filtersExpanded && (
            <div className={styles.mobileFilterContent}>
              {/* Search Section */}
              <div className={styles.mobileSearchSection}>
                <label className={styles.mobileSectionLabel}>Search:</label>
                <div className={styles.searchContainer}>
                  <input
                    type="text"
                    placeholder="Search shows..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onBlur={() => setFiltersExpanded(false)}
                    className={styles.searchInput}
                  />
                  {searchQuery && (
                    <>
                      <button
                        className={styles.doneSearch}
                        onClick={() => setFiltersExpanded(false)}
                        title="Close filters"
                      >
                        <Check size={16} />
                      </button>
                      <button
                        className={styles.clearSearch}
                        onClick={() => setSearchQuery('')}
                        title="Clear search"
                      >
                        ×
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Sort Section */}
              <div className={styles.mobileSortSection}>
                <label className={styles.mobileSectionLabel}>Sort by:</label>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className={styles.mobileSortSelect}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="date-newest">Date Added (Newest)</SelectItem>
                    <SelectItem value="date-oldest">Date Added (Oldest)</SelectItem>
                    <SelectItem value="rating-highest">Rating (Highest)</SelectItem>
                    <SelectItem value="rating-lowest">Rating (Lowest)</SelectItem>
                    <SelectItem value="title-az">Title (A-Z)</SelectItem>
                    <SelectItem value="title-za">Title (Z-A)</SelectItem>
                    <SelectItem value="release-newest">Release (Newest)</SelectItem>
                    <SelectItem value="release-oldest">Release (Oldest)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Filter Section */}
              <div className={styles.mobileFilterSection}>
                <label className={styles.mobileSectionLabel}>Show:</label>
                <div className={styles.mobileFilterButtons}>
                  <button 
                    className={`${styles.mobileFilterBtn} ${currentFilter === 'all' ? styles.active : ''}`}
                    onClick={() => {
                      setCurrentFilter('all');
                      setFiltersExpanded(false);
                    }}
                  >
                    <Grid size={18} />
                    <span>All Shows</span>
                  </button>
                  <button 
                    className={`${styles.mobileFilterBtn} ${currentFilter === 'watching' ? styles.active : ''}`}
                    onClick={() => {
                      setCurrentFilter('watching');
                      setFiltersExpanded(false);
                    }}
                  >
                    <Eye size={18} />
                    <span>Watching</span>
                  </button>
                  <button 
                    className={`${styles.mobileFilterBtn} ${currentFilter === 'waiting' ? styles.active : ''}`}
                    onClick={() => {
                      setCurrentFilter('waiting');
                      setFiltersExpanded(false);
                    }}
                  >
                    <Clock size={18} />
                    <span>Waiting</span>
                  </button>
                  <button 
                    className={`${styles.mobileFilterBtn} ${currentFilter === 'completed' ? styles.active : ''}`}
                    onClick={() => {
                      setCurrentFilter('completed');
                      setFiltersExpanded(false);
                    }}
                  >
                    <Check size={18} />
                    <span>Completed</span>
                  </button>
                  <button 
                    className={`${styles.mobileFilterBtn} ${currentFilter === 'want' ? styles.active : ''}`}
                    onClick={() => {
                      setCurrentFilter('want');
                      setFiltersExpanded(false);
                    }}
                  >
                    <Bookmark size={18} />
                    <span>Want to Watch</span>
                  </button>
                </div>
              </div>

              {/* View Toggle */}
              <div className={styles.mobileViewSection}>
                <label className={styles.mobileSectionLabel}>View:</label>
                <div className={styles.mobileViewButtons}>
                  <button 
                    className={`${styles.mobileViewBtn} ${viewMode === 'grid' ? styles.active : ''}`}
                    onClick={() => setViewMode('grid')}
                  >
                    <LayoutGrid size={18} />
                    <span>Grid</span>
                  </button>
                  <button 
                    className={`${styles.mobileViewBtn} ${viewMode === 'list' ? styles.active : ''}`}
                    onClick={() => setViewMode('list')}
                  >
                    <List size={18} />
                    <span>List</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {filteredShows.length === 0 ? (
              <Card className={styles.emptyCard}>
                <CardContent className={styles.emptyState}>
                  <p data-testid="empty-shows-msg">No shows in this category yet.</p>
                  <Button onClick={handleAddShow} className="mt-4">
                    Add Show
                  </Button>
                </CardContent>
              </Card>
            ) : viewMode === 'grid' ? (
              <div className={styles.showsGrid}>
                {filteredShows.map(show => (
                  <Card 
                    key={show.id} 
                    className={`${styles.showCard} ${styles.clickableCard}`} 
                    data-testid={`show-card-${show.id}`}
                    onClick={() => handleShowReviews(show)}
                  >
                    <CardContent className={styles.showCardContent}>
                      {(show.backdrop_url || show.image_url) && (
                        <div className={styles.showImage}>
                          <img 
                            src={show.backdrop_url || show.image_url} 
                            alt={`${show.title} TV show poster`}
                            loading="lazy"
                          />
                        </div>
                      )}
                      
                      <div className={styles.showHeader}>
                        <h3 data-testid={`show-title-${show.id}`}>{show.title}</h3>
                        <div className={styles.badgeContainer}>
                          <span className={styles.statusBadge}>{show.status}</span>
                          {show.badge && (
                            <span className={`${styles.episodeBadge} ${styles[`badge_${show.badge_type}`]}`}>
                              {show.badge}
                            </span>
                          )}
                        </div>
                      </div>

                      {(show.release_date || (show.genres && show.genres.length > 0)) && (
                        <div className={styles.showMetadataCompact}>
                          {show.release_date && (
                            <span className={styles.metadataYear}>{show.release_date.substring(0, 4)}</span>
                          )}
                          {show.genres && show.genres.length > 0 && (
                            <>
                              {show.release_date && <span className={styles.metadataSeparator}>•</span>}
                              <span className={styles.metadataGenres}>{show.genres.slice(0, 2).join(', ')}</span>
                            </>
                          )}
                        </div>
                      )}

                      {show.overview && (
                        <p className={styles.showOverview}>{show.overview}</p>
                      )}

                      <div className={styles.showDetails}>
                        {(show.streaming_service || show.custom_service) && (
                          <div className={styles.detailRow}>
                            <span className={styles.detailLabel}>Streaming on:</span>
                            <span className={styles.detailValue}>
                              <ServiceLabel name={show.streaming_service || show.custom_service} />
                            </span>
                          </div>
                        )}

                        {show.personal_rating && (
                          <div className={styles.detailRow}>
                            <span className={styles.detailLabel}>Your Rating:</span>
                            <span className={`${styles.detailValue} ${styles.rating}`}>
                              ⭐ {show.personal_rating}/10
                            </span>
                          </div>
                        )}

                        {show.last_shared && (
                          <div className={styles.detailRow}>
                            <span className={styles.detailLabel}>Last Shared:</span>
                            <span className={`${styles.detailValue} ${styles.lastShared}`}>
                              {new Date(show.last_shared).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                            </span>
                          </div>
                        )}

                        {showStatistics[show.title] && (
                          <div className={styles.statisticsSection}>
                            {showStatistics[show.title].all_users.count === 0 ? (
                              <div className={styles.firstReviewMessage}>
                                No one has reviewed this yet
                              </div>
                            ) : showStatistics[show.title].all_users.count === 1 && showStatistics[show.title].current_user_reviewed ? (
                              <div className={styles.firstReviewMessage}>
                                ⭐ You are the first to Review
                              </div>
                            ) : (
                              <>
                                {showStatistics[show.title].friends.average && (
                                  <div className={styles.statRow}>
                                    <span className={styles.statLabel}>👥 Friends&apos; Average:</span>
                                    <span className={styles.statValue}>
                                      ⭐ {showStatistics[show.title].friends.average}/10
                                      <span className={styles.statCount}>({showStatistics[show.title].friends.count})</span>
                                    </span>
                                  </div>
                                )}
                                {showStatistics[show.title].all_users.average && (
                                  <div className={styles.statRow}>
                                    <span className={styles.statLabel}>🌎 All Users&apos; Average:</span>
                                    <span className={styles.statValue}>
                                      ⭐ {showStatistics[show.title].all_users.average}/10
                                      <span className={styles.statCount}>({showStatistics[show.title].all_users.count})</span>
                                    </span>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        )}

                        {show.personal_review && (
                          <div className={styles.reviewSection}>
                            <span className={styles.detailLabel}>Your Review:</span>
                            <p className={styles.reviewText}>{show.personal_review}</p>
                          </div>
                        )}
                      </div>

                      <div className={styles.showActions} onClick={(e) => e.stopPropagation()}>
                        {show.status !== 'Completed' && (
                          <Button
                            data-testid={`complete-btn-${show.id}`}
                            className="complete-btn icon-btn"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMarkCompleted(show);
                            }}
                            title="Mark as Completed"
                          >
                            <CheckCircle size={18} />
                          </Button>
                        )}
                        <Button
                          data-testid={`edit-btn-${show.id}`}
                          variant="outline"
                          size="sm"
                          className="icon-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(show);
                          }}
                          title="Edit Show"
                        >
                          <Edit2 size={18} />
                        </Button>
                        {show.personal_rating && show.personal_review && (
                          <Button
                            data-testid={`share-btn-${show.id}`}
                            size="sm"
                            className="icon-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleShare(show);
                            }}
                            title="Share Review"
                          >
                            <Share2 size={18} />
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          className="icon-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setRecommendingShow(show);
                          }}
                          title="Recommend to Friend"
                        >
                          <Send size={18} />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="icon-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEmailRecommendingShow(show);
                          }}
                          title="Recommend via Email"
                          data-testid={`email-recommend-btn-${show.id}`}
                        >
                          <Mail size={18} />
                        </Button>
                        <Button
                          data-testid={`delete-btn-${show.id}`}
                          variant="destructive"
                          size="sm"
                          className="icon-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteClick(show);
                          }}
                          disabled={deletingShowId === show.id}
                          title={deletingShowId === show.id ? "Deleting..." : "Delete Show"}
                        >
                          {deletingShowId === show.id ? (
                            <div className="animate-spin">⏳</div>
                          ) : (
                            <Trash2 size={18} />
                          )}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className={styles.showsList}>
                {filteredShows.map(show => (
                  <Card 
                    key={show.id} 
                    className={`${styles.showCardList} ${styles.clickableCard}`} 
                    data-testid={`show-card-${show.id}`}
                    onClick={() => handleShowReviews(show)}
                  >
                    <CardContent className={styles.showCardContentList}>
                      {show.image_url && (
                        <div className={styles.listPoster}>
                          <img 
                            src={show.image_url} 
                            alt={`${show.title} TV show poster`}
                            loading="lazy"
                          />
                        </div>
                      )}
                      
                      <div className={styles.listContent}>
                        <div className={styles.listHeader}>
                          <h3 className={styles.listTitle}>{show.title}</h3>
                          {show.badge && (
                            <span className={`${styles.episodeBadge} ${styles.episodeBadgeSmall} ${styles[`badge_${show.badge_type}`]}`}>
                              {show.badge}
                            </span>
                          )}
                        </div>
                        {show.release_date && (
                          <div className={styles.listYear}>
                            {show.release_date.substring(0, 4)}
                          </div>
                        )}
                        {show.genres && show.genres.length > 0 && (
                          <div className={styles.listGenres}>
                            {show.genres.slice(0, 2).join(', ')}
                          </div>
                        )}
                        {show.overview && (
                          <p className={styles.listOverview}>{show.overview}</p>
                        )}
                        {(show.streaming_service || show.custom_service) && (
                          <div className={styles.listStreaming}>
                            <ServiceLabel name={show.streaming_service || show.custom_service} prefix="📺 " />
                          </div>
                        )}
                        {show.personal_rating && (
                          <div className={styles.listRating}>
                            ⭐ {show.personal_rating}/10
                          </div>
                        )}
                        {show.last_shared && (
                          <div className={styles.listLastShared}>
                            Last Shared: {new Date(show.last_shared).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                          </div>
                        )}
                        {showStatistics[show.title] && showStatistics[show.title].all_users.count > 1 && (
                          <div className={styles.listStats}>
                            {showStatistics[show.title].friends.average && (
                              <span className={styles.listStat}>
                                👥 ⭐ {showStatistics[show.title].friends.average}/10
                              </span>
                            )}
                            {showStatistics[show.title].all_users.average && (
                              <span className={styles.listStat}>
                                🌎 ⭐ {showStatistics[show.title].all_users.average}/10
                              </span>
                            )}
                          </div>
                        )}

                        <div className={styles.listActions} onClick={(e) => e.stopPropagation()}>
                          <div className={styles.listStatusIcon} title={show.status}>
                            {getStatusIcon(show.status)}
                          </div>
                          {show.status !== 'Completed' && (
                            <Button
                              data-testid={`complete-btn-${show.id}`}
                              className="complete-btn icon-btn"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMarkCompleted(show);
                              }}
                              title="Mark as Completed"
                            >
                              <CheckCircle size={18} />
                            </Button>
                          )}
                          <Button
                            data-testid={`edit-btn-${show.id}`}
                            variant="outline"
                            size="sm"
                            className="icon-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(show);
                            }}
                            title="Edit Show"
                          >
                            <Edit2 size={18} />
                          </Button>
                          {show.personal_rating && show.personal_review && (
                            <Button
                              data-testid={`share-btn-${show.id}`}
                              size="sm"
                              className="icon-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleShare(show);
                              }}
                              title="Share Review"
                            >
                              <Share2 size={18} />
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            className="icon-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              setRecommendingShow(show);
                            }}
                            title="Recommend to Friend"
                          >
                            <Send size={18} />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="icon-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEmailRecommendingShow(show);
                            }}
                            title="Recommend via Email"
                            data-testid={`email-recommend-list-btn-${show.id}`}
                          >
                            <Mail size={18} />
                          </Button>
                          <Button
                            data-testid={`delete-btn-${show.id}`}
                            variant="destructive"
                            size="sm"
                            className="icon-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteClick(show);
                            }}
                            disabled={deletingShowId === show.id}
                            title={deletingShowId === show.id ? "Deleting..." : "Delete Show"}
                          >
                            {deletingShowId === show.id ? (
                              <div className="animate-spin">⏳</div>
                            ) : (
                              <Trash2 size={18} />
                            )}
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
      </div>

      {showModal && (
        <ShowModal
          mode={modalMode}
          show={editingShow}
          user={user}
          onClose={() => {
            setShowModal(false);
            setEditingShow(null);
          }}
          onSuccess={loadShows}
        />
      )}

      {sharingReview && (
        <ShareReviewModal
          show={sharingReview}
          onClose={() => setSharingReview(null)}
          onShared={() => {
            setSharingReview(null);
          }}
        />
      )}

      {selectedShowForReviews && (
        <ShowReviewsModal
          show={selectedShowForReviews}
          onClose={() => setSelectedShowForReviews(null)}
        />
      )}

      {recommendingShow && (
        <RecommendShowModal
          show={recommendingShow}
          onClose={() => setRecommendingShow(null)}
        />
      )}

      {emailRecommendingShow && (
        <EmailRecommendModal
          show={emailRecommendingShow}
          user={user}
          onClose={() => setEmailRecommendingShow(null)}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!showToDelete} onOpenChange={(open) => !open && setShowToDelete(null)}>
        <DialogContent className={styles.deleteDialog}>
          <DialogHeader>
            <DialogTitle>Delete Show</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{showToDelete?.title}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className={styles.deleteDialogFooter}>
            <Button variant="outline" onClick={() => setShowToDelete(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </>
  );
};

export default MyShows;